import { SourceQuestion, SourceWithSourceQuestions, TaxReturnType, TaxReturnSubType } from "../../../../../swaggerServices";
import { Instruction } from "./instruction";
import { calculationSource } from "./transient";
import { literalSource } from "./transient";
import { TypeSubtypeLookup } from "../../../../shared/util/tax-return-type-subtype";

export enum LiteralType {
  Text = 'Text',
  Paragraph = 'Paragraph',
  Number = 'Number',
  Date = 'Date',
}

export interface TextLiteral {
  type: LiteralType.Text;
  value: string;
}

export interface NumberLiteral {
  type: LiteralType.Number;
  value: number | null;
}

export const dateLiteralFormat = 'DD/MM/YYYY';
export interface DateLiteral {
  type: LiteralType.Date;
  value: string;
  format: 'DD/MM/YYYY';
}

export enum SubtypeMode {
  Prior = 'Prior',
  Same = 'Same',
  Specific = 'Specific',
  Chain = 'Chain', 
}

export interface TaxReturnSourceOptions {
  priorYear: boolean;
  type: TaxReturnType | null;
  subtypeMode: SubtypeMode;
  subtypeValue: TaxReturnSubType | null; // present if subtypeMode === Specific. NULL if Same, Prior or Chain
  subtypeChain: TaxReturnSubType[] | null; // present if subtypeMode === Chain. NULL if Same, Prior or Specific
}

export interface CalcInputData {
  source: string;
  field: string | number | TextLiteral | NumberLiteral | DateLiteral | null;
  py?: boolean; // (false = same year)
  type?: string; // id | null (null = same type)
  subtype?: string; // id | null (null = same subtype)
  chain?: boolean; // (if true, then subtype is latest from chain involving subtype)
}

export class CalcInput {
  private static unknownSourceFromId(sourceId: string): SourceWithSourceQuestions {
    return {
      id: Number(sourceId),
      name: `Unknown Source (${sourceId})`,
      type: "unknown",
      sourceQuestions: [],
    };
  }

  private static optionsFromData({ type, subtype, chain, py }: any, typeSubtypeLookup: TypeSubtypeLookup) {
    const priorYear = (py === true);
    const typeId = type;
    const t: TaxReturnType = typeId ? typeSubtypeLookup.types.get(typeId.toString()) : null;

    let subtypeMode: SubtypeMode = null;
    let subtypeValue: TaxReturnSubType = null;
    let subtypeChain: TaxReturnSubType[] = null;
    if (chain && subtype) {
      subtypeMode = SubtypeMode.Chain;
      subtypeChain = typeSubtypeLookup.chains.get(subtype.toString());
    } else if (subtype === "prior") {
      subtypeMode = SubtypeMode.Prior;
      subtypeValue = null;
    } else if (subtype) {
      subtypeMode = SubtypeMode.Specific;
      subtypeValue = typeSubtypeLookup.subtypes.get(subtype.toString());
    } else {
      subtypeMode = SubtypeMode.Same;
      subtypeValue = null;
    }

    return {
      priorYear,
      type: t,
      subtypeMode,
      subtypeValue,
      subtypeChain,
    };
  }

  static fromData(data: CalcInputData, sources: Map<string, SourceWithSourceQuestions>, typeSubtypeLookup: TypeSubtypeLookup, isGlobalCalcPlan: boolean) {
    if (data.source === "literal") {
      const field: TextLiteral | NumberLiteral | DateLiteral | any = data.field;
      return new CalcInput(literalSource, field, null, isGlobalCalcPlan);
    }
    if (data.source === "calculation") {
      const field: number | any = data.field;
      return new CalcInput(calculationSource, field, null, isGlobalCalcPlan);
    }
    const source = sources.get(data.source);
    const resolvedSource = source || CalcInput.unknownSourceFromId(data.source);
    const f: number | any = data.field;
    let options = null;
    if (resolvedSource.type === "taxReturn") {
      options = CalcInput.optionsFromData(data, typeSubtypeLookup);
    }
    return new CalcInput(resolvedSource, resolvedSource.sourceQuestions.find(q => q.id === data.field) || f, options, isGlobalCalcPlan);
  }

  constructor(
    public source: SourceWithSourceQuestions,
    public field: SourceQuestion | Instruction | TextLiteral | NumberLiteral | DateLiteral | number | string | null,
    public options: TaxReturnSourceOptions | null,
    public isGlobalCalcPlan: boolean
  ) {
  }
}
